import { ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { addMonths, format, getMonth, getYear } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { isMonthStepEnabled } from "web_myplann/Shared/Components/FlatRow/isMonthStepEnabled";
import { useDateFnsLocale } from "web_myplann/Shared/Hooks/useDateFnsLocale";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type Props = {
	currentMonth: Date;
	minDate?: Date;
	maxDate?: Date;
	onMonthChange: (month: Date) => void;
};

export const FlatRowDatePanelMonthHeader = ({
	currentMonth,
	minDate,
	maxDate,
	onMonthChange,
}: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const locale = useDateFnsLocale();

	const previousEnabled = isMonthStepEnabled(
		getYear(currentMonth),
		getMonth(currentMonth),
		-1,
		minDate,
		maxDate,
	);
	const nextEnabled = isMonthStepEnabled(
		getYear(currentMonth),
		getMonth(currentMonth),
		1,
		minDate,
		maxDate,
	);

	const handlePrevious = () => {
		if (!previousEnabled) return;
		onMonthChange(addMonths(currentMonth, -1));
	};

	const handleNext = () => {
		if (!nextEnabled) return;
		onMonthChange(addMonths(currentMonth, 1));
	};

	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			px={theme.spacingTokens.lg}
			pb={theme.spacingTokens.md}
		>
			<ButtonBase
				onClick={handlePrevious}
				aria-disabled={!previousEnabled}
				aria-label={t(Resources.Common.DatePicker.previousMonth)}
				sx={{ p: theme.spacingTokens.sm, borderRadius: theme.radii.full }}
			>
				<ChevronLeft
					size={theme.iconSize.md}
					color={
						previousEnabled ? theme.palette.blue[600] : theme.palette.grey[300]
					}
				/>
			</ButtonBase>
			<Typography
				variant="bodyMedium"
				color={theme.palette.grey[800]}
				textTransform="capitalize"
			>
				{format(currentMonth, DateFormat.MonthYearLong, { locale })}
			</Typography>
			<ButtonBase
				onClick={handleNext}
				aria-disabled={!nextEnabled}
				aria-label={t(Resources.Common.DatePicker.nextMonth)}
				sx={{ p: theme.spacingTokens.sm, borderRadius: theme.radii.full }}
			>
				<ChevronRight
					size={theme.iconSize.md}
					color={
						nextEnabled ? theme.palette.blue[600] : theme.palette.grey[300]
					}
				/>
			</ButtonBase>
		</Stack>
	);
};
